import React, { useState } from 'react';
import Modal from '../Modal';
import { useToast } from '../Toast';
import { useUpsertReferenceMutation } from '../../store/api/localApi';

interface ReferenceFormProps {
  isOpen: boolean;
  onClose: () => void;
  staffUserId: string;
}

/**
 * Record a reference check. Admin-only, always — a reference is by
 * definition validated by someone other than the staff member it's about, so
 * there is no self-submit variant (see upsert-reference handler). This form
 * is only ever mounted for an admin viewer.
 */
const ReferenceForm: React.FC<ReferenceFormProps> = ({ isOpen, onClose, staffUserId }) => {
  const { showSuccess, showError } = useToast();
  const [upsertReference, { isLoading }] = useUpsertReferenceMutation();

  const [refereeName, setRefereeName] = useState('');
  const [refereeOrganisation, setRefereeOrganisation] = useState('');
  const [note, setNote] = useState('');

  const resetForm = () => {
    setRefereeName('');
    setRefereeOrganisation('');
    setNote('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await upsertReference({
        staffUserId,
        refereeName: refereeName.trim(),
        ...(refereeOrganisation.trim() ? { refereeOrganisation: refereeOrganisation.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }).unwrap();
      showSuccess('Reference recorded');
      resetForm();
      onClose();
    } catch (err: any) {
      showError(err.data?.error || 'Failed to record reference');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record Reference" testId="reference-form-modal">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="reference-referee-name"
            className="block text-sm font-medium text-gray-700"
          >
            Referee name
          </label>
          <input
            id="reference-referee-name"
            type="text"
            required
            data-testid="reference-referee-name-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={refereeName}
            onChange={(e) => setRefereeName(e.target.value)}
          />
        </div>
        <div>
          <label
            htmlFor="reference-referee-organisation"
            className="block text-sm font-medium text-gray-700"
          >
            Referee organisation
          </label>
          <input
            id="reference-referee-organisation"
            type="text"
            data-testid="reference-referee-organisation-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={refereeOrganisation}
            onChange={(e) => setRefereeOrganisation(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="reference-note" className="block text-sm font-medium text-gray-700">
            Note
          </label>
          <textarea
            id="reference-note"
            rows={3}
            data-testid="reference-note-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !refereeName.trim()}
            data-testid="reference-form-submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ReferenceForm;
